/** Classifies the current PowerPoint selection into the subset Get Insights
 *  supports (chart/table/image); anything else resolves to `null` so callers
 *  render one consistent "select a chart, table, or image" message. */
import { logError, scrubMessage } from "../../../../../telemetry";
import type { InsightsSelectionContext, InsightsSelectionType } from "../../../types";
import { findChartRId, findImageREmbed, loadActiveSlideDocument } from "./insightsExtractor";

const INSIGHTS_CONNECTOR = "deckorate";

function logInsightsError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: INSIGHTS_CONNECTOR, reason },
  );
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

/** Maps a PowerPoint.Shape's `type` to the subset Get Insights supports. */
function classifyByRawType(apiType: unknown): InsightsSelectionType | null {
  const t = typeof apiType === "string" ? apiType : "";
  if (t === "Table") return "table";
  if (t === "Image" || t === "LinkedImage") return "image";
  if (t === "Chart") return "chart";
  return null;
}

/** Content placeholders (Insert Table/Chart/Picture icon) report
 *  shape.type === "Placeholder"; the real kind is on placeholderFormat.type. */
function classifyByPlaceholderType(placeholderType: unknown): InsightsSelectionType | null {
  const t = typeof placeholderType === "string" ? placeholderType : "";
  if (t === "Table") return "table";
  if (t === "Chart") return "chart";
  if (t === "Picture" || t === "BitmapPicture") return "image";
  return null;
}

/** A generic "Content" placeholder doesn't reveal its contents, so probe for
 *  a real table (positive rowCount); anything else is left for `probeHasChartOrImage`. */
async function probeHasTable(
  shape: PowerPoint.Shape,
  context: PowerPoint.RequestContext,
): Promise<boolean> {
  try {
    const table = shape.getTable();
    table.load("rowCount");
    await context.sync();
    return typeof table.rowCount === "number" && table.rowCount > 0;
  } catch (e) {
    logInsightsError("insights-probe-table", "InsightsProbeTableError", e);
    return false;
  }
}

/** Last-resort check when `probeHasTable` doesn't match: PowerPoint exposes no
 *  live chart/picture sub-object, so this checks the slide's raw OOXML for a
 *  matching graphicFrame/pic. Any failure falls back to "unsupported". */
async function probeHasChartOrImage(
  shapeName: string,
  slideIndex: number,
): Promise<InsightsSelectionType | null> {
  try {
    const { slideDoc } = await loadActiveSlideDocument(slideIndex);
    if (findChartRId(slideDoc, shapeName)) return "chart";
    if (findImageREmbed(slideDoc, shapeName)) return "image";
    return null;
  } catch (e) {
    logInsightsError("insights-probe-chart-image", "InsightsProbeChartImageError", e);
    return null;
  }
}

/** getSelectedShapes() requires PowerPointApi 1.5. Returns false only when the
 *  host explicitly reports it unsupported; an unavailable capability check
 *  falls through to let the real API call fail inside detectInsightsSelection. */
function isSelectedShapesApiExplicitlyUnsupported(): boolean {
  try {
    const isSetSupported = Office?.context?.requirements?.isSetSupported;
    if (typeof isSetSupported !== "function") return false;
    return !isSetSupported("PowerPointApi", "1.5");
  } catch (e) {
    logInsightsError("insights-capability-check", "InsightsCapabilityCheckError", e);
    return false;
  }
}

/** Active slide index (1-based) via the Office Common API; defaults to 1 on
 *  any failure so a transient host error never blocks selection detection. */
function getActiveSlideIndex(): Promise<number> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.SlideRange, (result) => {
        try {
          const slides = (result.value as { slides?: Array<{ index: number }> } | undefined)?.slides;
          resolve(slides?.[0]?.index ?? 1);
        } catch (e) {
          logInsightsError("insights-active-slide-index", "InsightsActiveSlideIndexError", e);
          resolve(1);
        }
      });
    } catch (e) {
      logInsightsError("insights-active-slide-index", "InsightsActiveSlideIndexError", e);
      resolve(1);
    }
  });
}

export async function detectInsightsSelection(): Promise<InsightsSelectionContext | null> {
  if (!hasPpt()) {
    return null;
  }
  if (isSelectedShapesApiExplicitlyUnsupported()) {
    return null;
  }

  try {
    const slideIndex = await getActiveSlideIndex();

    return await ppt().run(async (context) => {
      if (typeof context.presentation.getSelectedShapes !== "function") {
        return null;
      }

      const selected = context.presentation.getSelectedShapes();
      selected.load("items");
      await context.sync();

      // Nothing selected — fall back to the whole current slide, captured as
      // an image (see extractSelectionContent's "slide" branch).
      if (selected.items.length === 0) {
        const selection: InsightsSelectionContext = {
          type: "slide",
          slideIndex,
          shapeId: `slide-${slideIndex}`,
          shapeName: `Slide ${slideIndex}`,
        };
        return selection;
      }

      if (selected.items.length !== 1) {
        return null;
      }

      const shape = selected.items[0]!;
      shape.load("id,name,type");
      await context.sync();

      let type = classifyByRawType(shape.type);

      if (!type && String(shape.type) === "Placeholder") {
        try {
          const placeholderFormat = (shape as unknown as {
            placeholderFormat: { type: unknown; load(s: string): void };
          }).placeholderFormat;
          placeholderFormat.load("type");
          await context.sync();
          type = classifyByPlaceholderType(placeholderFormat.type);
        } catch (e) {
          // Some hosts don't expose placeholderFormat — fall through to the
          // table/chart/image probes below instead of failing here.
          logInsightsError("insights-placeholder-format", "InsightsPlaceholderFormatError", e);
        }
      }

      // Not resolved by shape.type or placeholderFormat.type — e.g. some hosts
      // report embedded charts/graphicFrame content as "GraphicFrame" rather
      // than the "Chart" literal classifyByRawType expects. Fall back to
      // checking for actual table data, then to the raw slide XML for a
      // matching chart/picture, regardless of the shape's reported type.
      if (!type && (await probeHasTable(shape, context))) {
        type = "table";
      }
      if (!type) {
        type = await probeHasChartOrImage(shape.name ?? "", slideIndex);
      }

      if (!type) return null;

      const selection: InsightsSelectionContext = {
        type,
        slideIndex,
        shapeId: shape.id,
        shapeName: shape.name ?? "",
      };
      return selection;
    });
  } catch (e) {
    logInsightsError("insights-detect", "InsightsDetectError", e);
    return null;
  }
}
